import { commerceApiOrigin } from '../config'

export type StorefrontCategory = {
  id: string
  slug: string
  name: string
}

export type StorefrontContent = {
  hero: {
    eyebrow: string
    titleLine1: string
    titleLine2: string
    description: string
    imageUrl: string
    darkImageUrl: string
    imageAlt: string
  }
  featured: {
    title: string
    linkLabel: string
  }
  story: {
    eyebrow: string
    title: string
    description: string
    buttonLabel: string
    imageUrl: string
    imageAlt: string
  }
  legalLinkLabel: string
  returnsLinkLabel: string
  categories: StorefrontCategory[]
}

const fallbackImage = '/media/mudaii-temporary-object-v1.png'

export const defaultStorefrontContent: StorefrontContent = {
  hero: {
    eyebrow: 'MUDAII OBJECTS',
    titleLine1: '役に立たなくても、',
    titleLine2: 'そこにあるとうれしい。',
    description: '小さなアートと、ちょっと変なもの。',
    imageUrl: fallbackImage,
    darkImageUrl: '/media/mudaii-temporary-dark-hero-v1.png',
    imageAlt: 'ダミー商品イメージ'
  },
  featured: {
    title: '最近、見つけたもの',
    linkLabel: 'すべて見る'
  },
  story: {
    eyebrow: 'NOT QUITE USEFUL',
    title: 'なんだこれ',
    description: '見るたびに、ちょっと笑える。\n理由はないけど、気になる。',
    buttonLabel: 'もっと見る',
    imageUrl: fallbackImage,
    imageAlt: 'MUDAIIのダミーイメージ'
  },
  legalLinkLabel: '特定商取引法に基づく表記',
  returnsLinkLabel: '返品・返金について',
  categories: [
    { id: 'fallback-place', slug: 'place', name: '置く' },
    { id: 'fallback-use', slug: 'use', name: '使う' },
    { id: 'fallback-gift', slug: 'gift', name: '贈る' },
    { id: 'fallback-what', slug: 'what-is-this', name: 'なんだこれ' }
  ]
}

type UnknownRecord = Record<string, unknown>

function asRecord(value: unknown): UnknownRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as UnknownRecord : null
}

function text(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback
}

function imageUrl(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback
  const candidate = value.trim()
  if (candidate.startsWith('/') && !candidate.startsWith('//')) return candidate
  if (/^https:\/\//iu.test(candidate)) return candidate
  return fallback
}

function firstValue(records: Array<UnknownRecord | null>, keys: string[]): unknown {
  for (const record of records) {
    if (!record) continue
    for (const key of keys) {
      if (key in record) return record[key]
    }
  }
  return undefined
}

function normalizeCategory(value: unknown, index: number): StorefrontCategory | null {
  const record = asRecord(value)
  if (!record) return null
  if (typeof record.status === 'string' && record.status !== 'active') return null
  const name = text(record.name, '')
  const slug = text(record.slug, '')
  if (!name || !slug) return null
  const id = text(record.id, slug || `category-${index + 1}`)
  return { id, slug, name }
}

function normalize(value: unknown): StorefrontContent {
  const root = asRecord(value)
  if (!root) return defaultStorefrontContent
  const payload = asRecord(root.content) || asRecord(root.data) || root
  const home = asRecord(payload.home) || asRecord(payload.homepage) || payload
  const hero = asRecord(home.hero)
  const featured = asRecord(home.featured) || asRecord(home.recentProducts)
  const story = asRecord(home.story)
  const heroRecords = [hero, home, payload, root]
  const featuredRecords = [featured, home, payload, root]
  const storyRecords = [story, home, payload, root]

  const categoryValue = root.categories ?? payload.categories ?? home.categories
  const categories = Array.isArray(categoryValue)
    ? categoryValue.map(normalizeCategory).filter((category): category is StorefrontCategory => category !== null)
    : []
  const uniqueCategories = categories.filter((category, index, all) =>
    all.findIndex((candidate) => candidate.slug === category.slug) === index
  )

  return {
    hero: {
      eyebrow: text(firstValue(heroRecords, ['eyebrow', 'heroEyebrow', 'hero_eyebrow']), defaultStorefrontContent.hero.eyebrow),
      titleLine1: text(firstValue(heroRecords, ['titleLine1', 'title_line_1', 'heroTitleLine1', 'heroTitleLineOne', 'hero_title_line_1', 'hero_title_line_one']), defaultStorefrontContent.hero.titleLine1),
      titleLine2: text(firstValue(heroRecords, ['titleLine2', 'title_line_2', 'heroTitleLine2', 'heroTitleLineTwo', 'hero_title_line_2', 'hero_title_line_two']), defaultStorefrontContent.hero.titleLine2),
      description: text(firstValue(heroRecords, ['description', 'heroDescription', 'hero_description']), defaultStorefrontContent.hero.description),
      imageUrl: imageUrl(firstValue(heroRecords, ['imageUrl', 'image_url', 'heroImageUrl', 'hero_image_url']), defaultStorefrontContent.hero.imageUrl),
      darkImageUrl: imageUrl(firstValue(heroRecords, ['darkImageUrl', 'dark_image_url', 'heroDarkImageUrl', 'hero_dark_image_url']), defaultStorefrontContent.hero.darkImageUrl),
      imageAlt: text(firstValue(heroRecords, ['imageAlt', 'image_alt', 'heroImageAlt', 'hero_image_alt']), defaultStorefrontContent.hero.imageAlt)
    },
    featured: {
      title: text(firstValue(featuredRecords, ['title', 'featuredTitle', 'featuredHeading', 'featured_title', 'featured_heading']), defaultStorefrontContent.featured.title),
      linkLabel: text(firstValue(featuredRecords, ['linkLabel', 'link_label', 'featuredLinkLabel', 'featured_link_label']), defaultStorefrontContent.featured.linkLabel)
    },
    story: {
      eyebrow: text(firstValue(storyRecords, ['eyebrow', 'storyEyebrow', 'story_eyebrow']), defaultStorefrontContent.story.eyebrow),
      title: text(firstValue(storyRecords, ['title', 'storyTitle', 'story_title']), defaultStorefrontContent.story.title),
      description: text(firstValue(storyRecords, ['description', 'storyDescription', 'story_description']), defaultStorefrontContent.story.description),
      buttonLabel: text(firstValue(storyRecords, ['buttonLabel', 'button_label', 'storyButtonLabel', 'storyLinkLabel', 'story_button_label', 'story_link_label']), defaultStorefrontContent.story.buttonLabel),
      imageUrl: imageUrl(firstValue(storyRecords, ['imageUrl', 'image_url', 'storyImageUrl', 'story_image_url']), defaultStorefrontContent.story.imageUrl),
      imageAlt: text(firstValue(storyRecords, ['imageAlt', 'image_alt', 'storyImageAlt', 'story_image_alt']), defaultStorefrontContent.story.imageAlt)
    },
    legalLinkLabel: text(firstValue([home, payload, root], ['legalLinkLabel', 'legal_link_label']), defaultStorefrontContent.legalLinkLabel),
    returnsLinkLabel: text(firstValue([home, payload, root], ['returnsLinkLabel', 'returns_link_label']), defaultStorefrontContent.returnsLinkLabel),
    categories: uniqueCategories.length > 0 ? uniqueCategories : defaultStorefrontContent.categories
  }
}

export async function loadStorefrontContent(): Promise<StorefrontContent> {
  if (!commerceApiOrigin) return defaultStorefrontContent
  try {
    const response = await fetch(commerceApiOrigin + '/v1/storefront/content', {
      credentials: 'omit',
      headers: { accept: 'application/json' }
    })
    if (!response.ok) return defaultStorefrontContent
    return normalize(await response.json())
  } catch {
    return defaultStorefrontContent
  }
}
